import React, { Component } from 'react';
import styled from 'styled-components';
import { SceneView } from '@react-navigation/core';
import ImageBW from '../../components/ImageBoundsWrapper';
import Animated from 'animated/lib/targets/react-dom';
/** Screens */
import Screens from 'shared/screens';
import UpdateTerms from '../../screens/UpdateTerms/UpdateTerms';

/** Persistent Components */
import NavigationBar from '../NavigationBar';
import NavigationConstants from 'shared/components/LobbyOverlay/NavigationConstants';
import SideMenu from '../SideMenu';
import BottomBar from '../BottomBar';

/** Sizes */
import { height as BotHeight } from 'shared/components/BottomBar/sizes';

/** Utility */
import ThemeContext from 'shared/context/ThemeContext';
import theme from 'shared/assets/style/theme';

/** Modal Components */
import { Modal } from 'react-bootstrap';
import modalComponents from '../modals';

/** Notification Components */
import Toast from '../Toast';
import ActivityIndicator from '../ActivityIndicator/ActivityIndicator';
import SLobbyOverlay from 'shared/components/LobbyOverlay';
import Utils from 'shared/utils';
import QueryParamProxy from 'shared/utils/queryParamProxy';
import RouteParamConstants from 'shared/utils/routeParamConstants';
import TimestampManagerComponent from '../TimestampManagerComponent';
import preload from '../../lib/load/preload';
import audio from 'shared/utils/audio';

/** Tooltip for web */
import Tooltip from '../../components/Tooltip';

/** Animations */
import { BlackOverlay } from './styledComponents';
import FlyingCoinsAnimation from './FlyingCoinsAnimation';

const WHITE_LISTED_MOUNT_MODALS = [Screens.CurrencySelector];

const tooltip = tooltipDetails => {
  return (
    <Tooltip
      visible
      style={tooltipDetails.style ? tooltipDetails.style : {}}
      arrowStyle={tooltipDetails.arrowStyle ? tooltipDetails.arrowStyle : {}}
      containerStyle={tooltipDetails.containerStyle ? tooltipDetails.containerStyle : {}}
      tipStyle={tooltipDetails.tipStyle ? tooltipDetails.tipStyle : {}}
      source={tooltipDetails.source ? tooltipDetails.source : null}
      ibw={tooltipDetails.ibw ? tooltipDetails.ibw : null}
      imageStyle={tooltipDetails.gameImageStyle ? tooltipDetails.gameImageStyle : {}}
      titleStyle={tooltipDetails.titleStyle ? tooltipDetails.titleStyle : {}}
      subTitleStyle={tooltipDetails.subTitleStyle ? tooltipDetails.subTitleStyle : {}}
      iconType={tooltipDetails.iconType ? tooltipDetails.iconType : null}
      displayText={tooltipDetails.title}
      displayTextStyle={tooltipDetails.displayTextStyle}
      subTitle={tooltipDetails.subTitle}
      actionHandler={tooltipDetails.actionHandler}
      dividerStyle={tooltipDetails.dividerStyle ? tooltipDetails.dividerStyle : {}}
      animationClassName={tooltipDetails.animationState}
      popUpType={tooltipDetails.popUpType}
      innerStyle={tooltipDetails.innerStyle ?? {}}
    />
  );
};

export default class LobbyOverlay extends Component {
  state = {
    modal: undefined,
    showModal: false,
    isFlyingCoinsAnimationStopped: true,
    isCoinsGlowAnimationStopped: true,
    isBlackLayoutVisible: false,
  };

  startTimedBonusAnimation = () => {
    // starting animations
    this.setState({ isFlyingCoinsAnimationStopped: false, isBlackLayoutVisible: true });
    setTimeout(() => {
      this.setState({ isCoinsGlowAnimationStopped: false });
    }, 1250);

    // setting timeouts for stopping them
    setTimeout(() => {
      this.setState({ isFlyingCoinsAnimationStopped: true });
    }, 1850);
    setTimeout(() => {
      this.setState({ isCoinsGlowAnimationStopped: true, isBlackLayoutVisible: false });
    }, 2200);
  };

  async componentDidMount() {
    const initialModal = QueryParamProxy.findParam(RouteParamConstants.PARAM_MODAL);
    if (WHITE_LISTED_MOUNT_MODALS.includes(initialModal)) {
      this.setState({ modal: Screens.CurrencySelector });
    }
    QueryParamProxy.addListener(QueryParamProxy.QUERY_PARAM_CHANGE, this.handleParamsChange);
  }

  componentWillUnmount() {
    clearInterval(this.setTimerInterval);
    QueryParamProxy.removeListener(QueryParamProxy.QUERY_PARAM_CHANGE, this.handleParamsChange);
  }

  getModalComponent = modal => {
    if (modal === null || modal === undefined) {
      return false;
    }

    const component = modalComponents[modal];
    if (!component) throw new Error(`Unsupported Modal Type "${modal}"`);
    if (this.state.showModal && !this.state.popUpOpened) {
      if (this.state.modal) {
        audio.playSfx('popUp', true, 1);
      } else {
        audio.playSfx('popUp');
      }
      this.setState({ popUpOpened: true });
    }
    return component;
  };

  handleParamsChange = params => {
    if (params.hasOwnProperty(RouteParamConstants.PARAM_MODAL)) {
      this.setState({
        // IIFE, defines modal on the setState object if it should be changed.
        ...(() => {
          if (params[RouteParamConstants.PARAM_MODAL] !== undefined) {
            return {
              modal: params[RouteParamConstants.PARAM_MODAL],
            };
          } else {
            return {};
          }
        })(),
        // Control whether to hide/show based on new param state
        showModal: params[RouteParamConstants.PARAM_MODAL] === undefined ? false : true,
      });
    }
    if (!Utils.isEmpty(params[RouteParamConstants.PARAM_EXTERNAL_URL])) {
      window.open(params[RouteParamConstants.PARAM_EXTERNAL_URL], '_blank');
      QueryParamProxy.setQueryParams({
        [RouteParamConstants.PARAM_EXTERNAL_URL]: undefined,
      });
    }
  };

  __dismissModalGenerator = (ModalComponent, dismissModal) => {
    return () => {
      // If the modal was opened with a promise, accept it.
      // This is a no-op otherwise.
      audio.onClick();
      if (this.state.popUpOpened) {
        if (this.state.modal) {
          audio.playSfx('popUpReverse', true, 1);
        } else {
          audio.playSfx('popUpReverse');
        }
        this.setState({ popUpOpened: false });
      }

      let modalHandlerResponse = false;

      if (ModalComponent.DismissHandler) modalHandlerResponse = ModalComponent.DismissHandler.call();
      else dismissModal();

      QueryParamProxy.modalPromiseAccept(modalHandlerResponse);
    };
  };

  render() {
    const themeContext = this.context;
    return (
      <SLobbyOverlay
        initialModal={this.state.modal}
        preload={preload}
        {...this.props}
        render={args => {
          let {
            descriptor,
            loading,
            dismissModal,
            notifications,
            navState,
            showTooltip,
            tooltipDetails,
            showCustomModal,
            updatedTerms,
          } = args;
          const ModalComponent = this.getModalComponent(this.state.modal);

          let modalProps = {
            onHide: this.__dismissModalGenerator(ModalComponent, dismissModal),
            size: 'lg',
            'aria-labelledby': 'contained-modal-title-vcenter',
            centered: true,
          };

          if (ModalComponent && ModalComponent.AsModalProps) {
            modalProps = {
              ...modalProps,
              ...ModalComponent.AsModalProps,
            };
          }
          return (
            <>
              <BlackOverlay isVisible={this.state.isBlackLayoutVisible && this.props.animationFeature} />

              <FlyingCoinsAnimation isFlyingCoinsAnimationStopped={this.state.isFlyingCoinsAnimationStopped} />

              <TimestampManagerComponent />

              {loading && !updatedTerms && (
                <>
                  <ActivityIndicator />
                </>
              )}
              {!loading && !updatedTerms && (
                <>
                  <NavigationBar
                    navigation={this.props.navigation}
                    isCoinsGlowAnimationStopped={this.state.isCoinsGlowAnimationStopped}
                    isCoinsGlowAnimationEnabled={this.props.animationFeature}
                  />
                  {navState !== NavigationConstants.HIDE_BOTTOM && <SideMenu navigation={descriptor.navigation} />}
                  <MainSceneContainer
                    bottomState={NavigationConstants.HIDE_BOTTOM}
                    theme={themeContext.LobbyOverlay.MainSceneContainer}
                    className={'SceneContainer' + (NavigationConstants.HIDE_BOTTOM ? ' hideBottomBar' : '')}
                  >
                    <SceneView component={descriptor.getComponent()} navigation={descriptor.navigation} />
                  </MainSceneContainer>
                  <BottomBar
                    navigation={this.props.navigation}
                    startAnimationCallback={this.startTimedBonusAnimation}
                  />
                </>
              )}

              {updatedTerms && (
                <>
                  <UpdateTerms />
                </>
              )}

              {notifications.toasts && notifications.toasts.length > 0 && <Toast data={notifications.toasts[0]} />}
              {!updatedTerms && (
                <Modal show={this.state.showModal} {...modalProps}>
                  <>
                    {!ModalComponent.hideDefaultBackground && (
                      <div
                        className={`ModalBackground${ModalComponent.isTabbed ? ' Tabbed' : ''}${
                          ModalComponent.hideBeforeSelector || modalProps.hideBeforeSelector ? ' hideBefore' : ''
                        }`}
                      >
                        {showCustomModal ? (
                          <ImageBW
                            className="ModalBackgroundAsset"
                            as={Animated.div}
                            source={require(asset`modalBacker@3x.png`)}
                            ibw={require(asset`Modal_Backing_9Slice.ibw`)}
                            widthScale={0.5}
                          />
                        ) : (
                          <ImageBW
                            className="ModalBackgroundAsset"
                            as={Animated.div}
                            source={require(asset`Modal_Backing_9Slice@3x.png`)}
                            ibw={require(asset`Modal_Backing_9Slice.ibw`)}
                            widthScale={0.5}
                          />
                        )}
                      </div>
                    )}
                    {!ModalComponent.hideCloseButton && (
                      <div
                        className="ModalCloseButton"
                        onClick={this.__dismissModalGenerator(ModalComponent, dismissModal)}
                      >
                        <ImageBW
                          source={require(asset`CloseButton@3x.png`)}
                          ibw={require(asset`CloseButton.ibw`)}
                          manualAssetScale={3}
                          style={{
                            objectFit: 'contain',
                            height: '100%',
                            width: '100%',
                          }}
                          innerStyle={{
                            cursor: 'pointer',
                          }}
                          alt="Close"
                        />
                      </div>
                    )}
                    <Modal.Body>
                      {ModalComponent && (
                        <ModalComponent
                          dismissModal={this.__dismissModalGenerator(ModalComponent, dismissModal)}
                          navigation={this.props.navigation}
                        />
                      )}
                    </Modal.Body>
                    {ModalComponent.footer && <ModalFooterText>{ModalComponent.footer}</ModalFooterText>}
                  </>
                </Modal>
              )}
              {showTooltip && tooltipDetails && tooltip(tooltipDetails)}
            </>
          );
        }}
      />
    );
  }
}

LobbyOverlay.contextType = ThemeContext;

const MainSceneContainer = styled.div`
  ${({ theme }) => theme}
  padding-bottom: ${({ bottomState }) => (bottomState ? '0px' : BotHeight)};
`;

const ModalFooterText = styled.p`
  z-index: 100;
  margin-bottom: -24px;
  width: 100%;
  text-align: center;
  font-weight: 600;
  color: ${theme.palette.common[29]};
`;

